import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { StepProgressIndicator } from '../components/Onboarding/StepProgressIndicator';
import { CompanyStep1Name } from '../components/Onboarding/CompanyStep1Name';
import { CompanyStep2Locations } from '../components/Onboarding/CompanyStep2Locations';
import { ProgressModal } from '../components/Onboarding/ProgressModal';
import { ExitConfirmationModal } from '../components/Onboarding/ExitConfirmationModal';

const TOTAL_STEPS = 2; // Company name + Locations


export const CompanyOnboardingPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState(1);


    // Modal State
    const [isProcessing, setIsProcessing] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [processingError, setProcessingError] = useState<string>('');
    const [showExitConfirmation, setShowExitConfirmation] = useState(false);
    const isBlockingRef = useRef(true);

    // Form State - Step 1: Company name and type
    const [hirerType, setHirerType] = useState<'company' | 'recruiter'>('company');
    const [companyName, setCompanyName] = useState('');

    // Form State - Step 2: Locations
    const [companyLocation, setCompanyLocation] = useState(''); // Where they are based
    const [hiringLocation, setHiringLocation] = useState(''); // Where they are hiring


    // Prevent browser back/forward and page unload (Copied from OnboardingPage)
    useEffect(() => {
        isBlockingRef.current = !isComplete;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isBlockingRef.current) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        window.history.pushState({ onboarding: true }, '', window.location.href);

        const handlePopState = (e: PopStateEvent) => {
            if (isBlockingRef.current) {
                window.history.pushState({ onboarding: true }, '', window.location.href);
                setShowExitConfirmation(true);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isComplete]);

    // Check if we have pending signup data (don't create account yet - wait for onboarding completion)
    useEffect(() => {
        const pendingSignup = sessionStorage.getItem('pendingCompanySignup');

        if (user) {
            // User is already logged in - redirect to dashboard
            navigate('/dashboard');
            return;
        }

        if (!pendingSignup) {
            // No pending signup and no user - redirect to signup
            navigate('/hiring-signup');
        }
    }, [user, navigate]);

    const handleExitConfirm = useCallback(async () => {
        setShowExitConfirmation(false);
        isBlockingRef.current = false;
        setIsComplete(true);
        sessionStorage.removeItem('pendingCompanySignup');
        navigate('/');
    }, [navigate]);

    const handleExitCancel = useCallback(() => {
        setShowExitConfirmation(false);
        window.history.pushState({ onboarding: true }, '', window.location.href);
    }, []);

    const handleModalClose = () => {
        setIsProcessing(false);
        setProcessingError('');
    };

    // Handle onboarding completion with location data
    const handleSubmit = async () => {
        setIsProcessing(true);
        setIsComplete(false);
        setProcessingError('');

        try {
            // 1. Get pending signup credentials from sessionStorage
            const pendingSignupData = sessionStorage.getItem('pendingCompanySignup');
            if (!pendingSignupData) {
                throw new Error('No signup credentials found. Please start from the signup page.');
            }

            const { email, password, userType } = JSON.parse(pendingSignupData);

            // 2. Create Supabase account
            console.log('Creating Supabase account for hirer...');
            const { data: signupData, error: signupError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        user_type: userType,
                        full_name: companyName,
                    },
                },
            });

            if (signupError) throw signupError;

            // Check if session was created (email confirmation disabled)
            if (!signupData.session) {
                throw new Error('Email confirmation is required. Please check your email and try logging in after confirmation.');
            }

            const userId = signupData.user?.id;
            const userEmail = signupData.user?.email;

            if (!userId) {
                throw new Error('Failed to get user ID after signup');
            }

            console.log('Account created successfully:', userId);

            // 3. Insert into hirers table with name, type, and location fields
            const { error: hirerError } = await supabase
                .from('hirers')
                .insert({
                    user_id: userId,
                    supabase_uid: userId,
                    email: userEmail,
                    name: companyName,
                    hirer_type: hirerType,
                    country: companyLocation, // Where they are based
                    primary_hiring_location: hiringLocation, // Where they are hiring
                })
                .select()
                .single();

            if (hirerError) throw hirerError;

            // 4. Clear pending signup data
            sessionStorage.removeItem('pendingCompanySignup');

            // Success
            setIsComplete(true);
            setTimeout(() => {
                navigate('/dashboard');
            }, 1500);

        } catch (err: any) {
            console.error('Onboarding error:', err);
            setProcessingError(err.message || 'Failed to complete onboarding');
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <CompanyStep1Name
                        hirerType={hirerType}
                        companyName={companyName}
                        onHirerTypeUpdate={setHirerType}
                        onUpdate={setCompanyName}
                        onNext={() => setStep(2)} // Go to locations step
                    />
                );
            case 2:
                return (
                    <CompanyStep2Locations
                        companyLocation={companyLocation}
                        hiringLocation={hiringLocation}
                        onUpdate={(companyLoc, hiringLoc) => {
                            setCompanyLocation(companyLoc);
                            setHiringLocation(hiringLoc);
                        }}
                        onBack={() => setStep(1)}
                        onComplete={handleSubmit} // Complete onboarding
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            {/* Header with Logo and Exit Option */}
            <header className="p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src="/JobVitalLogo.png"
                            alt="JobVital Logo"
                            className="w-8 h-8 object-contain"
                        />
                        <span className="text-xl font-bold text-gray-900">JobVital</span>
                    </div>

                    {/* Exit Button */}
                    <button
                        onClick={() => setShowExitConfirmation(true)}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors px-4 py-2 rounded-lg hover:bg-gray-100"
                    >
                        Exit to Home
                    </button>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center px-6 pb-12">
                <div className="w-full max-w-2xl">


                    {/* Step Progress Indicator */}
                    <StepProgressIndicator currentStep={step} totalSteps={TOTAL_STEPS} />

                    {/* Current Step Content */}
                    <div className="animate-fadeIn">{renderStep()}</div>
                </div>
            </main>

            {/* Progress Modal */}
            {isProcessing && (
                <ProgressModal
                    isComplete={isComplete}
                    error={processingError}
                    onClose={handleModalClose}
                />
            )}

            {/* Exit Confirmation Modal */}
            <ExitConfirmationModal
                isOpen={showExitConfirmation}
                onConfirm={handleExitConfirm}
                onCancel={handleExitCancel}
            />
        </div>
    );
};
